/**
 * `?showcase=maps` — grey-box dev map with layered background (parallax), three tile bands built
 * from a `MapData.tilesets` declaration, foreground occluders, collision, exits, lights and every
 * interaction kind. A dev marker sprite is moved with arrows / WASD on the fixed 1/60 step; Z / Enter
 * / Space confirms; F simulates story-flag progress (gating), F1 toggles the region overlay, R returns
 * to the start spawn. Key presses are edge-captured from keyboard events so a tap that does not span
 * a fixed step still registers. Exposes `window.__mapsShowcase` for Playwright verification.
 */
import Phaser from 'phaser';
import { FixedStepper } from '@/core/clock';
import { Depth, FONT_BODY, GAME_H, GAME_W, TILE } from '@/core/config';
import { bus, Events } from '@/core/events';
import { SeededRng } from '@/core/rng';
import { SceneKeys } from '@/core/scenes/keys';
import type { Facing } from '@/core/types';
import type { MapData, MapExit, MapInteraction, MapSpawn } from '@/core/types/contracts';
import { InteractionSystem, type InteractPromptPayload } from '@/interaction/InteractionSystem';
import { slideMove } from './collision';
import { DEV_SKY_TEXTURE, DEV_TILESET_ID, ensureDevBackground, ensureDevTextures, makeDevMap } from './devkit';
import { createMapRuntime } from './runtime';

const SPEED = 2; // px per fixed step = 120 px/s
const FLAG_ORDER = ['TITLE', 'LETTER_OPEN', 'MEMORY_STORM', 'MEMORY_ISLAND_RITUAL', 'MEMORY_BLACK_WATER', 'MEMORY_FAREWELL', 'MEMORY_SWORD_TRAINING', 'LETTER_SENT', 'PROPHECY_REVEAL', 'HARBOR_FREE_ROAM'];
const DIR_CODES: Record<string, { dx: number; dy: number }> = {
  ArrowLeft: { dx: -1, dy: 0 }, KeyA: { dx: -1, dy: 0 },
  ArrowRight: { dx: 1, dy: 0 }, KeyD: { dx: 1, dy: 0 },
  ArrowUp: { dx: 0, dy: -1 }, KeyW: { dx: 0, dy: -1 },
  ArrowDown: { dx: 0, dy: 1 }, KeyS: { dx: 0, dy: 1 },
};
const CONFIRM_CODES = new Set(['KeyZ', 'Enter', 'Space', 'NumpadEnter']);

interface ShowcaseState {
  x: number; y: number; facing: Facing; prompt: InteractPromptPayload | null; log: string[]; flag: string; marks: string[]; map: string;
  overlay: boolean; camera: { scrollX: number; scrollY: number }; steps: number;
  /** Who renders the prompt label: the UI scene's pill (main entry) or the showcase's own text (standalone harness). */
  promptOwner: 'ui' | 'showcase'; ownPromptVisible: boolean;
}
interface ShowcaseRuntimeInfo {
  layers: string[]; backgrounds: number; foregrounds: number; tilesets: Record<string, string>; lights: number;
  worldWidth: number; worldHeight: number; parallax: number[];
}
export interface MapsShowcaseApi {
  state(): ShowcaseState;
  runtime(): ShowcaseRuntimeInfo;
  /** Plain copies of the map's regions (for harnesses to compute stand-in positions). */
  data(): { interactions: MapInteraction[]; exits: MapExit[]; spawns: MapSpawn[] };
  teleport(x: number, y: number, facing?: Facing): void;
  advanceFlag(): string;
  interact(): MapInteraction | null;
  /** Simulates a key tap by KeyboardEvent.code (consumed on the next fixed step). */
  press(code: string): void;
}
declare global { interface Window { __mapsShowcase?: MapsShowcaseApi } }

export function mapsShowcase(scene: Phaser.Scene): void {
  // A parallax-0.25 background must cover view + (world − view) × 0.25: 640 + 320 × 0.25 by 360 + 280 × 0.25.
  ensureDevBackground(scene, DEV_SKY_TEXTURE, 720, 432);
  const tex = ensureDevTextures(scene);
  const base = makeDevMap(30, 20, 1157, { id: 'showcase-maps' });
  const road = base.layout.roadRow;
  const { layout: _layout, ...mapOnly } = base;
  const data: MapData = {
    ...mapOnly,
    tilesets: [{ id: DEV_TILESET_ID, texture: tex.tiles, tileWidth: TILE, tileHeight: TILE }],
    background: [{ key: tex.sky, parallax: 0.25 }],
    foreground: [
      { key: tex.prop, x: 7 * TILE + 16, y: (road + 2) * TILE + 4 },
      { key: tex.prop, x: 15 * TILE + 16, y: road * TILE + 4 },
    ],
  };
  const rt = createMapRuntime(scene, data);
  scene.cameras.main.setBackgroundColor('#0b0f14');

  // ---- marker (stands in for the characters module's player)
  const spawn = rt.defaultSpawn();
  let x = spawn.x, y = spawn.y, facing: Facing = spawn.facing;
  const marker = scene.add.sprite(x, y, tex.marker, facing).setOrigin(0.5, 1).setDepth(Depth.WORLD_BASE + y);
  const place = (nx: number, ny: number, f: Facing) => {
    x = Math.round(nx); y = Math.round(ny); facing = f;
    marker.setPosition(x, y).setDepth(Depth.WORLD_BASE + y).setFrame(f);
  };

  // ---- lights: additive glow pools (the effects builder's LanternLayer does this in the real game)
  const glowRng = new SeededRng('maps-showcase-glow');
  const glows = rt.lights.map((l) => {
    const color = Phaser.Display.Color.HexStringToColor(l.color).color;
    const img = scene.add.image(l.x, l.y, tex.glow).setBlendMode(Phaser.BlendModes.ADD).setTint(color)
      .setDepth(Depth.LIGHTING).setAlpha(Math.min(1, l.intensity) * 0.75).setScale(Math.max(0.25, l.radius / 32));
    return { img, base: Math.min(1, l.intensity) * 0.75, flicker: !!l.flicker };
  });

  // ---- simulated story gate
  let flagIdx = 0;
  const marks = new Set<string>();
  const log: string[] = [];
  const pushLog = (line: string) => { log.push(line); if (log.length > 4) log.shift(); logText.setText(log.join('\n')); };
  const gate = {
    hasFlag: (f: string) => { const i = FLAG_ORDER.indexOf(f); return i >= 0 && i <= flagIdx; },
    hasMark: (m: string) => marks.has(m),
    setMark: (m: string) => { marks.add(m); drawOverlay(); },
  };
  const interaction = new InteractionSystem(data, gate, {
    onTalk: (i) => pushLog(`交谈 ${i.id} → 对白 ${i.dialogue}`),
    onObserve: (i) => pushLog(`查看 ${i.id} → 对白 ${i.dialogue}${i.once ? '（仅一次）' : ''}`),
    onTrigger: (i) => pushLog(`触发 ${i.id} → 演出 ${i.cinematic ?? i.dialogue ?? '(无)'}${i.auto ? '（自动）' : ''}`),
    onExit: (exit: MapExit | null, via?: MapInteraction) => {
      pushLog(`出口 ${exit?.id ?? via?.id ?? '?'} → ${exit?.target ?? '?'}:${exit?.spawn ?? '?'}（示范：传送回起点）`);
      scene.cameras.main.flash(160, 255, 255, 255);
      place(spawn.x, spawn.y, spawn.facing);
    },
  });

  // ---- camera: integer scroll, follows marker, bounded by the map
  const cam = scene.cameras.main;
  cam.setBounds(0, 0, rt.worldWidth, rt.worldHeight);
  cam.setRoundPixels(true);
  cam.startFollow(marker, true, 1, 1);
  const onPost = () => { cam.scrollX = Math.round(cam.scrollX); cam.scrollY = Math.round(cam.scrollY); };

  // ---- region overlay (F1)
  const overlay = scene.add.graphics().setDepth(Depth.OVERHEAD + 50);
  const labels: Phaser.GameObjects.Text[] = [];
  const labelStyle = { fontFamily: FONT_BODY, fontSize: '9px', color: '#ffffff', stroke: '#000000', strokeThickness: 2 };
  for (const i of data.interactions) labels.push(scene.add.text(i.x + 2, i.y + 1, i.id, labelStyle).setDepth(Depth.OVERHEAD + 51));
  for (const e of data.exits) labels.push(scene.add.text(e.x + 2, e.y + 1, `exit:${e.id}`, { ...labelStyle, color: '#ff8a8a' }).setDepth(Depth.OVERHEAD + 51));
  for (const s of data.spawns) labels.push(scene.add.text(s.x + 4, s.y - 10, s.id, { ...labelStyle, color: '#8af0ff' }).setDepth(Depth.OVERHEAD + 51));
  let overlayVisible = true;
  const drawOverlay = () => {
    overlay.clear();
    for (const i of data.interactions) {
      const enabled = interaction.isEnabled(i);
      const color = !enabled ? 0x9a9a9a : i.auto ? 0xffd23f : 0x3cff7a;
      overlay.fillStyle(color, enabled ? 0.2 : 0.08).fillRect(i.x, i.y, i.w, i.h);
      overlay.lineStyle(1, color, 0.9).strokeRect(i.x + 0.5, i.y + 0.5, i.w - 1, i.h - 1);
    }
    for (const e of data.exits) { overlay.fillStyle(0xff4040, 0.25).fillRect(e.x, e.y, e.w, e.h); overlay.lineStyle(1, 0xff4040, 1).strokeRect(e.x + 0.5, e.y + 0.5, e.w - 1, e.h - 1); }
    for (const s of data.spawns) overlay.fillStyle(0x8af0ff, 1).fillRect(s.x - 2, s.y - 2, 4, 4);
    for (const w of data.water ?? []) overlay.lineStyle(1, 0x6fd3c8, 0.6).strokeRect(w.x + 0.5, w.y + 0.5, w.w - 1, w.h - 1);
    for (const r of data.rainShelter ?? []) overlay.lineStyle(1, 0xffffff, 0.35).strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
    for (const l of data.lights ?? []) overlay.lineStyle(1, 0xffb347, 0.5).strokeCircle(l.x, l.y, l.radius);
  };
  drawOverlay();
  const setOverlay = (on: boolean) => { overlayVisible = on; overlay.setVisible(on); for (const l of labels) l.setVisible(on); };

  // ---- HUD (screen space)
  const hud = { fontFamily: FONT_BODY, fontSize: '12px', color: '#e2c48c' };
  scene.add.rectangle(0, 0, GAME_W, 40, 0x000000, 0.6).setOrigin(0, 0).setScrollFactor(0).setDepth(Depth.UI);
  scene.add.text(6, 4, 'maps 展示 · 方向键/WASD 移动 · Z 互动 · F 推进旗标 · F1 区域 · R 回起点', hud).setScrollFactor(0).setDepth(Depth.UI + 1);
  const stateText = scene.add.text(6, 22, '', { ...hud, fontSize: '11px', color: '#ffffff' }).setScrollFactor(0).setDepth(Depth.UI + 1);
  scene.add.rectangle(0, GAME_H - 62, GAME_W, 62, 0x000000, 0.6).setOrigin(0, 0).setScrollFactor(0).setDepth(Depth.UI);
  const logText = scene.add.text(6, GAME_H - 60, '', { ...hud, fontSize: '11px', color: '#ffffff', lineSpacing: 2 }).setScrollFactor(0).setDepth(Depth.UI + 1);
  // On the main entry the UI scene renders the `interact:prompt` pill above the player, so the showcase
  // must not draw a second label; the standalone harness (tools/maps/showcase.html) has no UI scene.
  const uiOwnsPrompt = () => scene.scene.isActive(SceneKeys.UI) === true;
  const promptText = scene.add.text(GAME_W / 2, GAME_H - 70, '', { fontFamily: FONT_BODY, fontSize: '14px', color: '#ffb347', backgroundColor: '#000000cc', padding: { x: 8, y: 3 } })
    .setOrigin(0.5, 1).setScrollFactor(0).setDepth(Depth.UI + 2).setVisible(false);
  let lastPrompt: InteractPromptPayload | null = null;
  const onPrompt = (p: { label?: string } | null) => {
    lastPrompt = p as InteractPromptPayload | null;
    promptText.setText(p ? `${p.label ?? ''}  [Z]` : '').setVisible(!!p && !uiOwnsPrompt());
  };
  bus.onTyped(Events.INTERACT_PROMPT, onPrompt);
  const refreshHud = () => {
    const t = rt.grid.tileAt(x, y);
    stateText.setText(`位置 (${x}, ${y}) 格 (${t.tx}, ${t.ty}) 朝向 ${facing} · 旗标 ${FLAG_ORDER[flagIdx]} · 标记 ${marks.size} · 水 ${rt.isWater(x, y) ? '是' : '否'} · 檐下 ${rt.isSheltered(x, y) ? '是' : '否'}`);
  };
  pushLog('走近区域会出现提示；走进红色出口会触发离开；面向门按 Z 也能离开。');

  // ---- input: held set + tap set from keyboard events (edge-safe), consumed on the fixed step
  const held = new Set<string>();
  const tapped = new Set<string>();
  const kb = scene.input.keyboard;
  const onKeyDown = (e: KeyboardEvent) => { if (!e.repeat) tapped.add(e.code); held.add(e.code); };
  const onKeyUp = (e: KeyboardEvent) => { held.delete(e.code); };
  kb?.on(Phaser.Input.Keyboard.Events.ANY_KEY_DOWN, onKeyDown);
  kb?.on(Phaser.Input.Keyboard.Events.ANY_KEY_UP, onKeyUp);
  kb?.addCapture(['UP', 'DOWN', 'LEFT', 'RIGHT', 'SPACE', 'F1']);
  const advanceFlag = () => { flagIdx = Math.min(flagIdx + 1, FLAG_ORDER.length - 1); pushLog(`旗标推进 → ${FLAG_ORDER[flagIdx]}`); drawOverlay(); return FLAG_ORDER[flagIdx] as string; };

  const stepper = new FixedStepper();
  let glowTick = 0;
  const step = () => {
    let dx = 0, dy = 0;
    for (const [code, d] of Object.entries(DIR_CODES)) {
      if (!held.has(code) && !tapped.has(code)) continue;
      if (d.dx && !dx) dx = d.dx;
      if (d.dy && !dy) dy = d.dy;
    }
    if (dx || dy) {
      const f: Facing = dx ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
      const r = slideMove(rt.grid, x, y, dx * SPEED, dy * SPEED);
      place(r.x, r.y, f);
    }
    let confirm = false;
    for (const code of tapped) {
      if (CONFIRM_CODES.has(code)) confirm = true;
      else if (code === 'KeyF') advanceFlag();
      else if (code === 'F1') setOverlay(!overlayVisible);
      else if (code === 'KeyR') place(spawn.x, spawn.y, spawn.facing);
    }
    tapped.clear();
    interaction.update(x, y, facing);
    if (confirm) interaction.tryInteract();
    if (++glowTick % 15 === 0) for (const g of glows) if (g.flicker) g.img.setAlpha(g.base * (0.82 + glowRng.range(0, 0.18)));
    refreshHud();
  };
  const onUpdate = (_t: number, delta: number) => stepper.tick(delta, step);
  scene.events.on(Phaser.Scenes.Events.UPDATE, onUpdate);
  scene.events.on(Phaser.Scenes.Events.POST_UPDATE, onPost);
  step();

  // ---- verification hook
  const api: MapsShowcaseApi = {
    state: () => ({
      x, y, facing, prompt: lastPrompt, log: [...log], flag: FLAG_ORDER[flagIdx] as string, marks: [...marks], map: data.id,
      overlay: overlayVisible, camera: { scrollX: cam.scrollX, scrollY: cam.scrollY }, steps: stepper.steps,
      promptOwner: uiOwnsPrompt() ? 'ui' : 'showcase', ownPromptVisible: promptText.visible,
    }),
    runtime: () => ({
      layers: rt.layers.map((l) => l.layer.name), backgrounds: rt.backgrounds.length, foregrounds: rt.foregrounds.length,
      tilesets: { ...rt.tilesets }, lights: rt.lights.length, worldWidth: rt.worldWidth, worldHeight: rt.worldHeight,
      parallax: (data.background ?? []).map((b) => b.parallax ?? 1),
    }),
    data: () => JSON.parse(JSON.stringify({ interactions: data.interactions, exits: data.exits, spawns: data.spawns })) as ReturnType<MapsShowcaseApi['data']>,
    teleport: (nx, ny, f = facing) => { place(nx, ny, f); interaction.update(x, y, facing); refreshHud(); },
    advanceFlag,
    interact: () => interaction.tryInteract(),
    press: (code) => { tapped.add(code); },
  };
  window.__mapsShowcase = api;

  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    scene.events.off(Phaser.Scenes.Events.UPDATE, onUpdate);
    scene.events.off(Phaser.Scenes.Events.POST_UPDATE, onPost);
    bus.offTyped(Events.INTERACT_PROMPT, onPrompt);
    kb?.off(Phaser.Input.Keyboard.Events.ANY_KEY_DOWN, onKeyDown);
    kb?.off(Phaser.Input.Keyboard.Events.ANY_KEY_UP, onKeyUp);
    kb?.removeCapture(['UP', 'DOWN', 'LEFT', 'RIGHT', 'SPACE', 'F1']);
    interaction.destroy();
    for (const g of glows) g.img.destroy();
    rt.destroy();
    if (window.__mapsShowcase === api) delete window.__mapsShowcase;
  });
}
